import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { PortfolioPricing, ProductElasticity, DemandCurve } from '@/api/client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { ProductSelector } from '@/components/ui/ProductSelector';
import { DataSourceSelector, type DataSourceId } from '@/components/ui/DataSourceSelector';

interface PricingSimulatorProps {
  clientId: string;
}

// Competitor reaction scenarios
type CompetitorReaction = 'none' | 'partial' | 'match' | 'counter' | 'custom';

interface CompetitorScenario {
  id: CompetitorReaction;
  label: string;
  description: string;
  reactionMultiplier: number; // How much competitor reacts (0 = none, 1 = full match, -1 = opposite)
}

const COMPETITOR_SCENARIOS: CompetitorScenario[] = [
  {
    id: 'none',
    label: 'No Reaction',
    description: 'Competitor maintains current pricing',
    reactionMultiplier: 0,
  },
  {
    id: 'partial',
    label: 'Partial Match',
    description: 'Competitor adjusts by 50% of your change',
    reactionMultiplier: 0.5,
  },
  {
    id: 'match',
    label: 'Full Match',
    description: 'Competitor matches your price move',
    reactionMultiplier: 1.0,
  },
  {
    id: 'counter',
    label: 'Counter-Move',
    description: 'Competitor moves opposite direction',
    reactionMultiplier: -0.5,
  },
];

export function PricingSimulator({ clientId }: PricingSimulatorProps) {
  const [selectedProduct, setSelectedProduct] = useState<ProductElasticity | null>(null);
  const [priceAdjustment, setPriceAdjustment] = useState(0);
  const [competitorReaction, setCompetitorReaction] = useState<CompetitorReaction>('none');
  const [customReactionPct, setCustomReactionPct] = useState(0);
  const [showCompetitorSection, setShowCompetitorSection] = useState(false);
  const [dataSource, setDataSource] = useState<DataSourceId>('blended');
  const [showDataSourceSelector, setShowDataSourceSelector] = useState(false);

  // Fetch pricing data (includes dataSource in query key for refetch on change)
  const { data: elasticityData, isLoading: elasticityLoading } = useQuery({
    queryKey: ['client', clientId, 'pricing', 'elasticity', dataSource],
    queryFn: () => apiClient.pricing.getElasticity(clientId),
    enabled: !!clientId,
  });

  const { data: portfolioData, isLoading: portfolioLoading } = useQuery({
    queryKey: ['client', clientId, 'pricing', 'portfolio', dataSource],
    queryFn: () => apiClient.pricing.getPortfolio(clientId),
    enabled: !!clientId,
  });

  // Fetch demand curve for selected product
  const { data: demandCurve, isLoading: demandLoading } = useQuery({
    queryKey: ['client', clientId, 'pricing', 'demand-curve', selectedProduct?.gtin, dataSource],
    queryFn: () => apiClient.pricing.getDemandCurve(clientId, selectedProduct!.gtin),
    enabled: !!clientId && !!selectedProduct,
  });

  const products = elasticityData?.products || [];

  // Get effective competitor reaction multiplier
  const getReactionMultiplier = (): number => {
    if (competitorReaction === 'custom') return customReactionPct / 100;
    return COMPETITOR_SCENARIOS.find(s => s.id === competitorReaction)?.reactionMultiplier ?? 0;
  };

  // Calculate simulated impact with competitor reactions
  const simulatedImpact = useMemo(() => {
    if (!selectedProduct || !demandCurve) return null;

    // Use current price from demand curve, not optimal
    const currentPrice = demandCurve.current?.price || 100;
    const newPrice = currentPrice * (1 + priceAdjustment / 100);
    const elasticity = selectedProduct.own_elasticity ?? selectedProduct.elasticity ?? -1.5;

    // Cross-elasticity: how our volume changes when competitor changes price
    // Positive value = competitor price up -> our volume up (substitutes)
    // Typical cross-elasticity for CPG substitutes: 0.3-0.8
    const crossElasticity = 0.5; // Could be made dynamic per product later

    // Calculate competitor's price change
    const reactionMultiplier = getReactionMultiplier();
    const competitorPriceChange = priceAdjustment * reactionMultiplier;

    // Volume change from OWN price change (own-price elasticity, typically negative)
    const volumeChangeFromOwn = elasticity * priceAdjustment;

    // Volume change from COMPETITOR price change (cross-elasticity, typically positive)
    // If competitor raises price, we gain volume; if they lower, we lose
    const volumeChangeFromCompetitor = crossElasticity * competitorPriceChange;

    // Net volume change
    const volumeChange = volumeChangeFromOwn + volumeChangeFromCompetitor;

    // Find closest point in demand curve
    const closestPoint = demandCurve.curve_points?.reduce((prev, curr) => {
      return Math.abs(curr.price - newPrice) < Math.abs(prev.price - newPrice) ? curr : prev;
    }, demandCurve.curve_points[0]);

    // Use current quantity from demand curve
    const currentQuantity = demandCurve.current?.quantity || 1000;
    const newQuantity = currentQuantity * (1 + volumeChange / 100);
    const currentRevenue = currentPrice * currentQuantity;
    const newRevenue = newPrice * newQuantity;
    const revenueChange = ((newRevenue - currentRevenue) / currentRevenue) * 100;

    // Calculate "No Reaction" baseline for comparison
    const baselineVolumeChange = elasticity * priceAdjustment;
    const baselineQuantity = currentQuantity * (1 + baselineVolumeChange / 100);
    const baselineRevenue = newPrice * baselineQuantity;
    const baselineRevenueChange = ((baselineRevenue - currentRevenue) / currentRevenue) * 100;

    return {
      currentPrice,
      newPrice,
      volumeChange,
      volumeChangeFromOwn,
      volumeChangeFromCompetitor,
      revenueChange,
      currentQuantity,
      newQuantity,
      currentRevenue,
      newRevenue,
      competitorPriceChange,
      crossElasticity,
      // Comparison with no-reaction baseline
      baselineVolumeChange,
      baselineRevenueChange,
      reactionImpact: revenueChange - baselineRevenueChange,
    };
  }, [selectedProduct, demandCurve, priceAdjustment, competitorReaction, customReactionPct]);

  if (elasticityLoading || portfolioLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-text-secondary">Loading pricing data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6 bg-gradient-to-r from-primary-500/10 to-surface-700">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Price Simulator</h2>
            <p className="text-sm text-text-secondary">
              Simulate price changes and see their impact on volume and revenue
            </p>
          </div>
          {/* Data Source Toggle */}
          <button
            onClick={() => setShowDataSourceSelector(!showDataSourceSelector)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-600/50 hover:bg-surface-600 transition-colors text-sm"
          >
            <span>🗂️</span>
            <span className="text-text-secondary">Data:</span>
            <span className="text-white font-medium">
              {dataSource === 'niq' ? 'NIQ' : dataSource === 'internal' ? 'Internal' : dataSource === 'survey' ? 'Survey' : 'Blended'}
            </span>
            <svg
              className={`w-4 h-4 text-text-tertiary transition-transform ${showDataSourceSelector ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Data Source Selector (Collapsible) */}
      {showDataSourceSelector && (
        <div className="card p-4 border-l-4 border-l-primary-500/50">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🗂️</span>
            <h3 className="text-sm font-medium text-white">Select Data Source</h3>
            <span className="text-xs text-text-tertiary">
              — Results may vary by source
            </span>
          </div>
          <DataSourceSelector
            selectedSource={dataSource}
            onSourceChange={(id) => {
              setDataSource(id);
              // Reset product selection when source changes
              setSelectedProduct(null);
              setPriceAdjustment(0);
            }}
            staleDays={30}
          />
        </div>
      )}

      {/* Product Selection - Horizontal Tab Selector */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Select Product</h3>
        <ProductSelector
          products={products.map((product) => ({
            id: product.gtin,
            name: product.name || product.gtin,
            price: demandCurve?.current?.price || 0,
            subtitle: `Elasticity: ${(product.own_elasticity ?? product.elasticity ?? 0).toFixed(2)} (${product.interpretation?.sensitivity || product.classification || 'N/A'})`,
          }))}
          selectedId={selectedProduct?.gtin || ''}
          onSelect={(id) => {
            const product = products.find(p => p.gtin === id);
            if (product) {
              setSelectedProduct(product);
              setPriceAdjustment(0);
            }
          }}
        />

        {/* Elasticity indicator for selected product */}
        {selectedProduct && (
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm text-text-secondary">Selected:</span>
            <span className="font-medium text-white">{selectedProduct.name || selectedProduct.gtin}</span>
            <span className={`text-sm px-2 py-0.5 rounded ${
              selectedProduct.interpretation?.sensitivity === 'elastic' || selectedProduct.interpretation?.sensitivity === 'highly_elastic'
                ? 'bg-danger-500/20 text-danger-400'
                : selectedProduct.interpretation?.sensitivity === 'inelastic'
                ? 'bg-success-500/20 text-success-400'
                : 'bg-warning-500/20 text-warning-400'
            }`}>
              {selectedProduct.interpretation?.sensitivity || selectedProduct.classification || 'N/A'}
            </span>
          </div>
        )}
      </div>

      {/* Price Adjustment */}
      {selectedProduct && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Price Adjustment</h3>
            <span className={`text-xl font-bold ${
              priceAdjustment > 0 ? 'text-success-400' : priceAdjustment < 0 ? 'text-danger-400' : 'text-text-secondary'
            }`}>
              {priceAdjustment > 0 ? '+' : ''}{priceAdjustment}%
            </span>
          </div>

          {/* Slider */}
          <div className="relative mb-6">
            <input
              type="range"
              min="-30"
              max="30"
              step="1"
              value={priceAdjustment}
              onChange={(e) => setPriceAdjustment(parseInt(e.target.value))}
              className="w-full h-2 bg-surface-600 rounded-lg appearance-none cursor-pointer slider-thumb"
            />
            <div className="flex justify-between text-xs text-text-tertiary mt-2">
              <span>-30%</span>
              <span className="text-primary-400">0%</span>
              <span>+30%</span>
            </div>
          </div>

          {/* Quick Adjustments */}
          <div className="flex flex-wrap gap-2">
            {[-20, -10, -5, 0, 5, 10, 20].map((val) => (
              <button
                key={val}
                onClick={() => setPriceAdjustment(val)}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  priceAdjustment === val
                    ? 'bg-primary-500 text-white'
                    : 'bg-surface-600 text-text-secondary hover:bg-surface-500'
                }`}
              >
                {val > 0 ? '+' : ''}{val}%
              </button>
            ))}
          </div>

          {/* Competitor Reaction Toggle */}
          <div className="mt-6 pt-4 border-t border-surface-600/50">
            <button
              onClick={() => setShowCompetitorSection(!showCompetitorSection)}
              className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors group"
            >
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${showCompetitorSection ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              <span className="group-hover:underline">
                {showCompetitorSection ? 'Hide competitor scenarios' : 'What if competitors react?'}
              </span>
              {competitorReaction !== 'none' && !showCompetitorSection && (
                <span className="ml-2 px-2 py-0.5 bg-warning-500/20 text-warning-400 text-xs rounded">
                  {COMPETITOR_SCENARIOS.find(s => s.id === competitorReaction)?.label || `${customReactionPct}%`}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Competitor Reaction Section */}
      {selectedProduct && showCompetitorSection && (
        <div className="card p-6 border-l-4 border-l-warning-500/50">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xl">🎯</span>
            <div>
              <h3 className="text-lg font-semibold text-white">Competitor Reaction Scenarios</h3>
              <p className="text-sm text-text-secondary">
                How might competitors respond to your price change?
              </p>
            </div>
          </div>

          {/* Scenario Selection */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {COMPETITOR_SCENARIOS.map((scenario) => (
              <button
                key={scenario.id}
                onClick={() => setCompetitorReaction(scenario.id)}
                className={`p-3 rounded-lg text-left transition-all ${
                  competitorReaction === scenario.id
                    ? 'bg-warning-500/20 border-2 border-warning-500'
                    : 'bg-surface-700/50 border-2 border-transparent hover:border-surface-500'
                }`}
              >
                <div className="font-medium text-white text-sm">{scenario.label}</div>
                <div className="text-xs text-text-tertiary mt-1">{scenario.description}</div>
                {scenario.reactionMultiplier !== 0 && priceAdjustment !== 0 && (
                  <div className={`text-xs mt-2 ${
                    scenario.reactionMultiplier * priceAdjustment > 0 ? 'text-success-400' : 'text-danger-400'
                  }`}>
                    Competitor: {scenario.reactionMultiplier * priceAdjustment > 0 ? '+' : ''}
                    {(scenario.reactionMultiplier * priceAdjustment).toFixed(0)}%
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Custom Reaction Input */}
          <div className="flex items-center gap-4 p-3 bg-surface-700/30 rounded-lg">
            <button
              onClick={() => setCompetitorReaction('custom')}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                competitorReaction === 'custom'
                  ? 'bg-warning-500 text-white'
                  : 'bg-surface-600 text-text-secondary hover:bg-surface-500'
              }`}
            >
              Custom
            </button>
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm text-text-secondary">Competitor adjusts by</span>
              <input
                type="number"
                min="-100"
                max="100"
                value={customReactionPct}
                onChange={(e) => {
                  setCustomReactionPct(parseInt(e.target.value) || 0);
                  setCompetitorReaction('custom');
                }}
                className="w-20 bg-surface-600 border border-surface-400/30 rounded px-2 py-1 text-white text-center text-sm"
              />
              <span className="text-sm text-text-secondary">% of your change</span>
            </div>
            {competitorReaction === 'custom' && priceAdjustment !== 0 && (
              <span className={`text-sm ${
                customReactionPct * priceAdjustment / 100 > 0 ? 'text-success-400' :
                customReactionPct * priceAdjustment / 100 < 0 ? 'text-danger-400' : 'text-text-secondary'
              }`}>
                = {(priceAdjustment * customReactionPct / 100) > 0 ? '+' : ''}
                {(priceAdjustment * customReactionPct / 100).toFixed(1)}%
              </span>
            )}
          </div>

          {/* Cross-Elasticity Explanation */}
          <div className="mt-4 p-3 bg-surface-800/50 rounded-lg text-xs text-text-tertiary">
            <span className="text-text-secondary font-medium">How it works:</span> When competitors change price,
            consumers may switch. Cross-elasticity of <span className="text-primary-400">0.5</span> means
            a 10% competitor price increase gains you ~5% volume.
          </div>
        </div>
      )}

      {/* Impact Simulation */}
      {selectedProduct && simulatedImpact && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Simulated Impact</h3>

          {demandLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" />
              <span className="ml-3 text-text-secondary">Calculating impact...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Metrics */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                  <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">New Price</div>
                  <div className="text-2xl font-bold text-white">
                    ${simulatedImpact.newPrice.toFixed(2)}
                  </div>
                  <div className="text-xs text-text-tertiary">
                    from ${simulatedImpact.currentPrice.toFixed(2)}
                  </div>
                </div>
                <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                  <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Volume Change</div>
                  <div className={`text-2xl font-bold ${
                    simulatedImpact.volumeChange >= 0 ? 'text-success-400' : 'text-danger-400'
                  }`}>
                    {simulatedImpact.volumeChange >= 0 ? '+' : ''}{simulatedImpact.volumeChange.toFixed(1)}%
                  </div>
                  <div className="text-xs text-text-tertiary">
                    Elasticity: {(selectedProduct?.own_elasticity ?? selectedProduct?.elasticity ?? 0).toFixed(2)}
                  </div>
                </div>
                <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                  <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Revenue Change</div>
                  <div className={`text-2xl font-bold ${
                    simulatedImpact.revenueChange >= 0 ? 'text-success-400' : 'text-danger-400'
                  }`}>
                    {simulatedImpact.revenueChange >= 0 ? '+' : ''}{simulatedImpact.revenueChange.toFixed(1)}%
                  </div>
                  <div className="text-xs text-text-tertiary">Net effect</div>
                </div>
                <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                  <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">New Volume</div>
                  <div className="text-2xl font-bold text-white">
                    {formatNumber(simulatedImpact.newQuantity)}
                  </div>
                  <div className="text-xs text-text-tertiary">
                    from {formatNumber(simulatedImpact.currentQuantity)}
                  </div>
                </div>
              </div>

              {/* Competitor Reaction Impact Breakdown */}
              {competitorReaction !== 'none' && simulatedImpact.competitorPriceChange !== 0 && (
                <div className="bg-warning-500/5 border border-warning-500/20 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-warning-400 mb-3 flex items-center gap-2">
                    <span>🎯</span>
                    Competitor Reaction Impact
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-text-tertiary mb-1">Your Price Change</div>
                      <div className={`font-medium ${priceAdjustment >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                        {priceAdjustment >= 0 ? '+' : ''}{priceAdjustment}%
                      </div>
                    </div>
                    <div>
                      <div className="text-text-tertiary mb-1">Competitor Response</div>
                      <div className={`font-medium ${
                        simulatedImpact.competitorPriceChange >= 0 ? 'text-success-400' : 'text-danger-400'
                      }`}>
                        {simulatedImpact.competitorPriceChange >= 0 ? '+' : ''}
                        {simulatedImpact.competitorPriceChange.toFixed(1)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-text-tertiary mb-1">Net Reaction Effect</div>
                      <div className={`font-medium ${
                        simulatedImpact.reactionImpact >= 0 ? 'text-success-400' : 'text-danger-400'
                      }`}>
                        {simulatedImpact.reactionImpact >= 0 ? '+' : ''}
                        {simulatedImpact.reactionImpact.toFixed(1)}% revenue
                      </div>
                    </div>
                  </div>

                  {/* Volume Attribution */}
                  <div className="mt-4 pt-3 border-t border-warning-500/10">
                    <div className="text-xs text-text-tertiary mb-2">Volume Change Breakdown</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-4 bg-surface-700 rounded-full overflow-hidden flex">
                        {/* Own price effect (typically negative when raising price) */}
                        <div
                          className={`h-full ${simulatedImpact.volumeChangeFromOwn >= 0 ? 'bg-success-500' : 'bg-danger-500'}`}
                          style={{
                            width: `${Math.min(100, Math.abs(simulatedImpact.volumeChangeFromOwn) / (Math.abs(simulatedImpact.volumeChangeFromOwn) + Math.abs(simulatedImpact.volumeChangeFromCompetitor) || 1) * 100)}%`
                          }}
                          title={`Own price effect: ${simulatedImpact.volumeChangeFromOwn.toFixed(1)}%`}
                        />
                        {/* Cross-elasticity effect */}
                        <div
                          className={`h-full ${simulatedImpact.volumeChangeFromCompetitor >= 0 ? 'bg-primary-500' : 'bg-warning-500'}`}
                          style={{
                            width: `${Math.min(100, Math.abs(simulatedImpact.volumeChangeFromCompetitor) / (Math.abs(simulatedImpact.volumeChangeFromOwn) + Math.abs(simulatedImpact.volumeChangeFromCompetitor) || 1) * 100)}%`
                          }}
                          title={`Competitor effect: ${simulatedImpact.volumeChangeFromCompetitor.toFixed(1)}%`}
                        />
                      </div>
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span className={simulatedImpact.volumeChangeFromOwn >= 0 ? 'text-success-400' : 'text-danger-400'}>
                        Own: {simulatedImpact.volumeChangeFromOwn >= 0 ? '+' : ''}{simulatedImpact.volumeChangeFromOwn.toFixed(1)}%
                      </span>
                      <span className={simulatedImpact.volumeChangeFromCompetitor >= 0 ? 'text-primary-400' : 'text-warning-400'}>
                        Competitor: {simulatedImpact.volumeChangeFromCompetitor >= 0 ? '+' : ''}{simulatedImpact.volumeChangeFromCompetitor.toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  {/* Comparison to No-Reaction Baseline */}
                  <div className="mt-3 text-xs">
                    <span className="text-text-tertiary">Without competitor reaction: </span>
                    <span className={simulatedImpact.baselineRevenueChange >= 0 ? 'text-success-400' : 'text-danger-400'}>
                      {simulatedImpact.baselineRevenueChange >= 0 ? '+' : ''}{simulatedImpact.baselineRevenueChange.toFixed(1)}% revenue
                    </span>
                    <span className="text-surface-400"> → </span>
                    <span className="text-text-secondary">
                      Reaction {simulatedImpact.reactionImpact >= 0 ? 'adds' : 'costs'}
                    </span>
                    <span className={simulatedImpact.reactionImpact >= 0 ? 'text-success-400' : 'text-danger-400'}>
                      {' '}{Math.abs(simulatedImpact.reactionImpact).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              {/* Demand Curve Visualization */}
              {demandCurve && (demandCurve.revenue_curve || demandCurve.curve_points) && (
                <div className="bg-surface-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-white mb-4">Revenue Curve</h4>
                  <DemandCurveChart
                    points={
                      // Prefer revenue_curve, fallback to calculating from curve_points
                      demandCurve.revenue_curve?.map(p => ({
                        price: p.price,
                        quantity: 0,
                        revenue: p.revenue
                      })) ||
                      demandCurve.curve_points.map(p => ({
                        price: p.price,
                        quantity: p.quantity,
                        revenue: p.revenue ?? p.price * p.quantity
                      }))
                    }
                    optimal={{
                      // Use revenue-maximizing price, not profit-maximizing
                      price: demandCurve.key_prices?.revenue_maximizing ?? demandCurve.optimal.price,
                      quantity: demandCurve.optimal.quantity,
                      revenue: demandCurve.optimal.revenue,
                    }}
                    currentPrice={simulatedImpact.currentPrice}
                    simulatedPrice={simulatedImpact.newPrice}
                  />
                </div>
              )}

              {/* Recommendation */}
              <div className={`border-l-4 rounded-r-lg p-4 ${
                simulatedImpact.revenueChange > 2 ? 'border-l-success-500 bg-success-500/5' :
                simulatedImpact.revenueChange < -2 ? 'border-l-danger-500 bg-danger-500/5' :
                'border-l-warning-500 bg-warning-500/5'
              }`}>
                <h4 className="text-sm font-semibold text-white mb-1">Recommendation</h4>
                <p className="text-sm text-text-secondary">
                  {competitorReaction !== 'none' && simulatedImpact.competitorPriceChange !== 0 ? (
                    // With competitor reaction
                    simulatedImpact.revenueChange > 2
                      ? `Even with competitor response (${simulatedImpact.competitorPriceChange >= 0 ? '+' : ''}${simulatedImpact.competitorPriceChange.toFixed(0)}%), this price change projects +${simulatedImpact.revenueChange.toFixed(1)}% revenue. Strong case to proceed.`
                      : simulatedImpact.revenueChange < -2
                      ? simulatedImpact.reactionImpact < -1
                        ? `Competitor reaction significantly erodes gains. Without response: ${simulatedImpact.baselineRevenueChange >= 0 ? '+' : ''}${simulatedImpact.baselineRevenueChange.toFixed(1)}%, but with response: ${simulatedImpact.revenueChange.toFixed(1)}%. Consider timing or alternative approach.`
                        : `This price change may reduce revenue by ${Math.abs(simulatedImpact.revenueChange).toFixed(1)}%. Competitor dynamics don't significantly change the outlook.`
                      : simulatedImpact.reactionImpact > 1
                        ? `Competitor matching your move actually helps: cross-elasticity effect adds ${simulatedImpact.reactionImpact.toFixed(1)}% to revenue. Category pricing power may be stronger than expected.`
                        : `Net impact is neutral (${simulatedImpact.revenueChange.toFixed(1)}%). Competitor response offsets some gains. Consider strategic objectives beyond immediate revenue.`
                  ) : (
                    // Without competitor reaction (original logic)
                    simulatedImpact.revenueChange > 2
                      ? `This price change is projected to increase revenue by ${simulatedImpact.revenueChange.toFixed(1)}%. Consider implementing this adjustment.`
                      : simulatedImpact.revenueChange < -2
                      ? `This price change may reduce revenue by ${Math.abs(simulatedImpact.revenueChange).toFixed(1)}%. Consider a smaller adjustment or alternative strategy.`
                      : `This price change has minimal revenue impact (${simulatedImpact.revenueChange.toFixed(1)}%). Consider your strategic objectives beyond revenue.`
                  )}
                </p>
                {competitorReaction === 'none' && priceAdjustment !== 0 && (
                  <p className="text-xs text-text-tertiary mt-2 italic">
                    💡 Tip: Expand "What if competitors react?" above to model different competitive responses.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Portfolio Pricing Summary */}
      {portfolioData && (portfolioData.portfolio_summary || portfolioData.architecture) && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Portfolio Pricing Overview</h3>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-surface-700/50 rounded-lg p-4 text-center">
              <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Price Range</div>
              <div className="text-xl font-bold text-white">
                ${(portfolioData.portfolio_summary?.price_range?.[0] ?? portfolioData.architecture?.price_range?.min ?? 0).toLocaleString()} - ${(portfolioData.portfolio_summary?.price_range?.[1] ?? portfolioData.architecture?.price_range?.max ?? 0).toLocaleString()}
              </div>
            </div>
            <div className="bg-surface-700/50 rounded-lg p-4 text-center">
              <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Average Price</div>
              <div className="text-xl font-bold text-primary-400">
                ${(portfolioData.portfolio_summary?.weighted_avg_price ?? portfolioData.architecture?.average_price ?? 0).toLocaleString()}
              </div>
            </div>
            <div className="bg-surface-700/50 rounded-lg p-4 text-center">
              <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Products</div>
              <div className="text-xl font-bold text-white">
                {portfolioData.portfolio_summary?.product_count ?? portfolioData.products?.length ?? 0}
              </div>
            </div>
          </div>

          {/* Product Price Gaps */}
          <h4 className="text-sm font-medium text-text-secondary mb-3">Products with Price Gap Opportunities</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {(portfolioData.products || [])
              .filter(p => Math.abs(p.price_gap ?? 0) > 0.05)
              .sort((a, b) => Math.abs(b.price_gap ?? 0) - Math.abs(a.price_gap ?? 0))
              .slice(0, 6)
              .map((product) => (
                <div key={product.gtin} className="flex items-center justify-between p-3 bg-surface-700/30 rounded-lg">
                  <span className="text-sm text-white truncate flex-1">{product.name || product.gtin}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-text-tertiary">
                      ${(product.current_price ?? 0).toFixed(2)} → ${(product.optimal_price ?? 0).toFixed(2)}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      (product.price_gap ?? 0) > 0 ? 'bg-success-500/20 text-success-400' : 'bg-danger-500/20 text-danger-400'
                    }`}>
                      {(product.price_gap ?? 0) > 0 ? '+' : ''}{((product.price_gap ?? 0) * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Demand Curve Chart Component
function DemandCurveChart({
  points,
  optimal,
  currentPrice,
  simulatedPrice,
}: {
  points: Array<{ price: number; quantity: number; revenue: number }>;
  optimal: { price: number; quantity: number; revenue: number } | undefined;
  currentPrice: number;
  simulatedPrice: number;
}) {
  const sortedPoints = [...points].sort((a, b) => a.price - b.price);
  const maxRevenue = Math.max(...sortedPoints.map(p => p.revenue));
  const minRevenue = Math.min(...sortedPoints.map(p => p.revenue));
  const revenueRange = maxRevenue - minRevenue || 1;
  const minPrice = sortedPoints[0]?.price || 0;
  const maxPrice = sortedPoints[sortedPoints.length - 1]?.price || 100;
  const priceRange = maxPrice - minPrice || 1;

  // SVG viewBox dimensions
  const width = 400;
  const height = 180;
  const padding = { top: 20, right: 20, bottom: 30, left: 20 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Convert data points to SVG coordinates
  const toX = (price: number) => padding.left + ((price - minPrice) / priceRange) * chartWidth;
  const toY = (revenue: number) => padding.top + chartHeight - ((revenue - minRevenue) / revenueRange) * chartHeight;

  // Build smooth curve path
  const pathData = sortedPoints.map((p, i) => {
    const x = toX(p.price);
    const y = toY(p.revenue);
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  const currentX = toX(currentPrice);
  const simulatedX = Math.max(padding.left, Math.min(width - padding.right, toX(simulatedPrice)));
  const optimalX = optimal ? toX(optimal.price) : null;

  return (
    <div className="relative" style={{ height: `${height + 20}px` }}>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
          <line
            key={i}
            x1={padding.left}
            y1={padding.top + chartHeight * (1 - ratio)}
            x2={width - padding.right}
            y2={padding.top + chartHeight * (1 - ratio)}
            stroke="#374151"
            strokeWidth="1"
            strokeDasharray={i === 0 ? "none" : "4,4"}
          />
        ))}

        {/* Revenue curve - filled area */}
        <path
          d={`${pathData} L ${toX(maxPrice)} ${padding.top + chartHeight} L ${toX(minPrice)} ${padding.top + chartHeight} Z`}
          fill="url(#curveGradient)"
          opacity="0.3"
        />

        {/* Revenue curve - line */}
        <path
          d={pathData}
          fill="none"
          stroke="#3B82F6"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Gradient definition */}
        <defs>
          <linearGradient id="curveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Current price marker */}
        <line
          x1={currentX}
          y1={padding.top}
          x2={currentX}
          y2={padding.top + chartHeight}
          stroke="#6B7280"
          strokeWidth="2"
          strokeDasharray="4,4"
        />
        <text x={currentX} y={padding.top - 5} textAnchor="middle" className="text-xs fill-gray-400">
          Current
        </text>

        {/* Simulated price marker */}
        {simulatedPrice !== currentPrice && (
          <>
            <line
              x1={simulatedX}
              y1={padding.top}
              x2={simulatedX}
              y2={padding.top + chartHeight}
              stroke="#8B5CF6"
              strokeWidth="2"
            />
            <text x={simulatedX} y={padding.top - 5} textAnchor="middle" className="text-xs fill-purple-400">
              Simulated
            </text>
          </>
        )}

        {/* Optimal price marker */}
        {optimalX && (
          <>
            <line
              x1={optimalX}
              y1={padding.top}
              x2={optimalX}
              y2={padding.top + chartHeight}
              stroke="#10B981"
              strokeWidth="2"
              opacity="0.7"
            />
            <circle cx={optimalX} cy={toY(optimal!.revenue)} r="5" fill="#10B981" />
            <text x={optimalX} y={padding.top + chartHeight + 15} textAnchor="middle" className="text-xs fill-green-400">
              Optimal
            </text>
          </>
        )}

        {/* Data points */}
        {sortedPoints.filter((_, i) => i % 3 === 0).map((p, i) => (
          <circle
            key={i}
            cx={toX(p.price)}
            cy={toY(p.revenue)}
            r="3"
            fill="#3B82F6"
          />
        ))}
      </svg>

      {/* X-axis labels */}
      <div className="absolute left-5 right-5 flex justify-between text-xs text-text-tertiary" style={{ bottom: 0 }}>
        <span>${minPrice.toLocaleString()}</span>
        <span>${((minPrice + maxPrice) / 2).toLocaleString()}</span>
        <span>${maxPrice.toLocaleString()}</span>
      </div>
    </div>
  );
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toFixed(0);
}
